import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SingleImageUploader } from '../../components/ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    image_url: '', 
    position: 0, 
    status: 'active' 
  });

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({ 
        name: category.name, 
        description: category.description || '', 
        image_url: category.image_url || '', 
        position: category.position, 
        status: category.status 
      });
    } else {
      setEditingCategory(null);
      setFormData({ 
        name: '', 
        description: '', 
        image_url: '', 
        position: categories.length + 1, 
        status: 'active' 
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData);
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/categories`, formData);
        toast.success('Categoría creada');
      }
      setIsModalOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría? Los productos asociados no serán eliminados.')) return;
    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-categories-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif text-white">Categorías</h1>
          <p className="text-white/60 text-sm">{categories.length} categorías</p>
        </div>
        <Button onClick={() => openModal()} className="btn-gold-solid" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat) => (
            <div 
              key={cat.id} 
              className="bg-[#121212] border border-white/10 overflow-hidden group"
              data-testid={`category-${cat.id}`}
            >
              <div className="aspect-video relative overflow-hidden bg-white/5">
                {cat.image_url ? (
                  <img 
                    src={cat.image_url} 
                    alt={cat.name} 
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20 text-sm">
                    Sin imagen
                  </div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button 
                    onClick={() => openModal(cat)} 
                    className="p-3 bg-gold text-black hover:bg-gold-hover transition-colors"
                    data-testid={`edit-category-${cat.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDelete(cat.id)} 
                    className="p-3 bg-red-500 text-white hover:bg-red-600 transition-colors"
                    data-testid={`delete-category-${cat.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-medium">{cat.name}</h3>
                  <span className={`px-2 py-0.5 text-xs ${
                    cat.status === 'active' 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-red-500/20 text-red-500'
                  }`}>
                    {cat.status === 'active' ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                <p className="text-white/60 text-sm line-clamp-2">
                  {cat.description || 'Sin descripción'}
                </p>
                <p className="text-white/40 text-xs mt-2">
                  Posición: {cat.position}
                </p>
              </div>
            </div>
          ))}
          
          {categories.length === 0 && (
            <div className="col-span-full text-center py-12">
              <p className="text-white/40 mb-4">No hay categorías</p>
              <Button onClick={() => openModal()} className="btn-gold">
                Crear primera categoría
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Category Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div>
              <label className="text-white/60 text-sm">Nombre *</label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                required 
                className="bg-black border-white/20 text-white mt-1"
                placeholder="Ej: Bikinis"
              />
            </div>
            
            <div>
              <label className="text-white/60 text-sm">Descripción</label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                className="bg-black border-white/20 text-white mt-1" 
                rows={3}
                placeholder="Descripción de la categoría..."
              />
            </div>
            
            <div>
              <label className="text-white/60 text-sm mb-2 block">Imagen de la Categoría</label>
              <SingleImageUploader
                imageUrl={formData.image_url}
                onImageChange={(url) => setFormData({ ...formData, image_url: url })}
                folder="davocado/categories"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm">Posición</label>
                <Input 
                  type="number" 
                  min="1"
                  value={formData.position} 
                  onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) || 1 })} 
                  className="bg-black border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <label className="text-white/60 text-sm">Estado</label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-black border-white/20 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/20">
                    <SelectItem value="active" className="text-white">Activa</SelectItem>
                    <SelectItem value="inactive" className="text-white">Inactiva</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-4 pt-4 border-t border-white/10">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsModalOpen(false)} 
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 btn-gold-solid">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingCategory ? 'Actualizar' : 'Crear')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
